import type { SurveyOrgFlags } from './dynamicSurveyNextStep';

// Metric keys that signal the onboarding survey has been answered (Sparta's
// METRIC_POINT_KEY).
const METRIC = {
  PHQ_2: 'phq-2',
  GAD_2: 'gad-2',
  PHQ_8: 'phq-8',
  GAD_7: 'gad-7',
  PREDIABETES_RISK: 'prediabetes-risk',
} as const;

/**
 * Whether the onboarding survey can be skipped (the user has already answered
 * it). Mirrors Sparta's checkHistoricalMetricPointsEpic, which resumes from the
 * most-recent metric point — extended with the paying org's screener flags so
 * an org that doesn't require phq-gad isn't gated on metrics it never posts.
 *
 * Completion signals, in order:
 *  - the full PHQ-8 / GAD-7 are present (phq-gad fully done), or
 *  - the preliminary PHQ-2 & GAD-2 both screened low (< 3), or
 *  - phq-gad is NOT required and the last required screener has posted its
 *    metric: when pre-diabetes is required, that's `prediabetes-risk`.
 *
 * `flags` defaults every absent value to "required", so behavior is identical
 * to before unless an org explicitly opts a screener out. An org that requires
 * NEITHER pre-diabetes nor phq-gad has no metric-bearing survey, so completion
 * can't be derived from metrics alone — it falls through to `false` (the driver
 * re-walks the short survey on a cold resolve); Sparta has the same limitation.
 *
 * `metricValues` is the most-recent value per metric key (see
 * fetchHistoricalMetricPoints). A missing PHQ-2/GAD-2 counts as "not low"
 * (Sparta defaults those to +Infinity), so a partial screen doesn't skip.
 */
export function isSurveyComplete(
  metricValues: Record<string, number>,
  flags: SurveyOrgFlags = {}
): boolean {
  if (metricValues[METRIC.PHQ_8] !== undefined || metricValues[METRIC.GAD_7] !== undefined) {
    return true;
  }
  const phq2 = metricValues[METRIC.PHQ_2] ?? Number.POSITIVE_INFINITY;
  const gad2 = metricValues[METRIC.GAD_2] ?? Number.POSITIVE_INFINITY;
  if (phq2 < 3 && gad2 < 3) {
    return true;
  }

  // phq-gad still required (or has elevated preliminary scores needing the
  // extended set) → not complete yet, exactly as before.
  if (flags.phqGadRequired !== false) {
    return false;
  }

  // phq-gad opted out: the last required screener is pre-diabetes (when
  // required), signaled by its `prediabetes-risk` metric point.
  if (flags.preDiabetesRequired !== false) {
    return metricValues[METRIC.PREDIABETES_RISK] !== undefined;
  }

  // Neither pre-diabetes nor phq-gad required → no metric to gate on.
  return false;
}
